import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeftRight, Plus, X, Check, AlertTriangle, ChevronDown, ChevronUp,
  MessageCircle, ExternalLink, RefreshCw, Info, Shield, Zap,
  Copy, Clock, Users, DollarSign, Wallet, Settings2
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { Link } from 'react-router-dom';
import {
  EXCHANGE_TAG, OFFER_KIND, PROTOCOL_FEE_PERCENT,
  calcProtocolFee, calcBondSats, parseOffer, calcTradePrice, formatBCH,
  PAYMENT_METHOD_LABELS, FIAT_CURRENCIES, DEFAULT_BOND_PERCENT,
  type Offer, type OfferSide, type PaymentMethod,
  PLATFORM_FEE_ADDRESS,
} from '../lib/exchange';
import UserBadges from '../components/UserBadges';
import { timeAgo } from '../lib/utils';

type Tab = 'buy' | 'sell' | 'my';

// ── Helpers ───────────────────────────────────────────────────────
function usdToSats(usd: number, bchPriceUSD: number): number {
  if (!bchPriceUSD) return 0;
  return Math.round((usd / bchPriceUSD) * 1e8);
}
function satsToUsd(sats: number, bchPriceUSD: number): number {
  if (!bchPriceUSD) return 0;
  return (sats / 1e8) * bchPriceUSD;
}

// ── Main Page ─────────────────────────────────────────────────────
export default function ExchangePage() {
  const { user, login } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { price: bchPriceUSD } = useBCHPrice();
  const [tab, setTab] = useState<Tab>('buy');
  const [showPostOffer, setShowPostOffer] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);

  useEffect(() => { tipWallet.init().catch(() => {}); }, []);

  const { events: offerEvents, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [OFFER_KIND], '#t': [EXCHANGE_TAG], limit: 200 }],
  });

  const offers = useMemo(() =>
    offerEvents
      .map(parseOffer)
      .filter((o): o is Offer => o !== null && o.status === 'open')
      .sort((a, b) => b.createdAt - a.createdAt),
    [offerEvents]
  );

  // "Buy BCH" tab = offers from people selling BCH (I can buy from them)
  const buyOffers  = offers.filter(o => o.side === 'sell');
  // "Sell BCH" tab = offers from people buying BCH (I can sell to them)
  const sellOffers = offers.filter(o => o.side === 'buy');
  const myOffers   = offers.filter(o => o.pubkey === user?.pubkey);
  const displayOffers = tab === 'buy' ? buyOffers : tab === 'sell' ? sellOffers : myOffers;

  const tabs: { id: Tab; label: string; sublabel: string; count: number; color: string }[] = [
    { id: 'buy',  label: 'Buy BCH',  sublabel: 'Pay with fiat',    count: buyOffers.length,  color: 'emerald' },
    { id: 'sell', label: 'Sell BCH', sublabel: 'Get fiat',         count: sellOffers.length, color: 'primary' },
    { id: 'my',   label: 'My Offers', sublabel: 'Manage yours',    count: myOffers.length,   color: 'muted' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-primary" /> P2P Exchange
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {user && (
              <button
                onClick={() => setShowPostOffer(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90"
              >
                <Plus className="w-3.5 h-3.5" /> Post Offer
              </button>
            )}
          </div>
        </div>

        {/* Live price */}
        {bchPriceUSD > 0 && (
          <p className="text-xs text-muted-foreground mb-2">
            BCH = <span className="font-semibold text-foreground">${bchPriceUSD.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
            <span className="mx-1.5 text-muted-foreground/40">·</span>
            {offers.length} active offer{offers.length !== 1 ? 's' : ''}
            <span className="mx-1.5 text-muted-foreground/40">·</span>
            0.5% fee
          </p>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-semibold transition-all border ${
                tab === t.id
                  ? t.id === 'buy'
                    ? 'bg-emerald-500 text-white border-emerald-500'
                    : t.id === 'sell'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-foreground border-border'
                  : 'bg-transparent text-muted-foreground border-transparent hover:bg-muted'
              }`}
            >
              <div>{t.label}{t.count > 0 ? ` (${t.count})` : ''}</div>
              <div className={`text-[10px] font-normal mt-0.5 ${tab === t.id && t.id !== 'my' ? 'opacity-80' : 'text-muted-foreground'}`}>
                {t.sublabel}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* How it works — collapsed by default */}
        <HowItWorksBanner />

        {/* Login prompt */}
        {!user && (
          <div className="glass-card text-center py-8">
            <ArrowLeftRight className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
            <p className="text-sm font-semibold mb-1">Log in to trade</p>
            <p className="text-xs text-muted-foreground mb-4">Connect your Nostr identity to post offers and start trading BCH peer-to-peer</p>
            <button onClick={login} className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:opacity-90">
              Log In
            </button>
          </div>
        )}

        {/* Empty state */}
        {user && !loading && displayOffers.length === 0 && (
          <div className="glass-card text-center py-10">
            <ArrowLeftRight className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
            <p className="text-sm font-semibold mb-1">
              {tab === 'my' ? 'No active offers' : `No ${tab === 'buy' ? 'sell' : 'buy'} offers yet`}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {tab === 'my' ? 'Post an offer to start trading' : 'Be the first to post one!'}
            </p>
            <button onClick={() => setShowPostOffer(true)} className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:opacity-90">
              Post Offer
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && offerEvents.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {/* Offer list */}
        {displayOffers.map(offer => (
          <OfferCard
            key={offer.eventId}
            offer={offer}
            bchPriceUSD={bchPriceUSD}
            isMe={offer.pubkey === user?.pubkey}
            onSelect={() => setSelectedOffer(offer)}
            onCancel={async () => {
              await publish({
                kind: OFFER_KIND,
                content: JSON.stringify({ ...offer, status: 'cancelled' }),
                tags: [['t', EXCHANGE_TAG], ['d', offer.dTag]],
                created_at: Math.floor(Date.now() / 1000),
              });
              setTimeout(refetch, 1500);
            }}
          />
        ))}
      </div>

      {showPostOffer && (
        <PostOfferModal
          bchPriceUSD={bchPriceUSD}
          onClose={() => setShowPostOffer(false)}
          onPosted={() => { setShowPostOffer(false); setTimeout(refetch, 1500); }}
          publish={publish}
          user={user}
        />
      )}

      {selectedOffer && (
        <TradeModal
          offer={selectedOffer}
          bchPriceUSD={bchPriceUSD}
          tipWallet={tipWallet}
          user={user}
          publish={publish}
          onClose={() => setSelectedOffer(null)}
        />
      )}
    </div>
  );
}

// ── How It Works (collapsed by default) ──────────────────────────
function HowItWorksBanner() {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass-card border-primary/20">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <span className="text-xs font-semibold flex items-center gap-1.5 text-primary">
          <Shield className="w-3.5 h-3.5" /> Non-custodial P2P — how it works
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-xs text-muted-foreground">
          {[
            { icon: '1\uFE0F\u20E3', text: 'Browse offers or post your own. Choose your price, payment method, and trade size in USD.' },
            { icon: '2\uFE0F\u20E3', text: 'Click an offer to start a trade. Send an encrypted Nostr DM to coordinate payment details.' },
            { icon: '3\uFE0F\u20E3', text: 'BCH is sent directly wallet-to-wallet. A 0.5% protocol fee is included automatically — no middleman.' },
            { icon: '4\uFE0F\u20E3', text: 'Both parties confirm completion. The more trades you complete, the more reputation you build.' },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-base leading-none mt-0.5 shrink-0">{s.icon}</span>
              <p>{s.text}</p>
            </div>
          ))}
          <div className="mt-1 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-amber-600 dark:text-amber-400">Start small. Always verify your counterparty's reputation before trading large amounts.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Offer Card ────────────────────────────────────────────────────
function OfferCard({ offer, bchPriceUSD, isMe, onSelect, onCancel }: {
  offer: Offer; bchPriceUSD: number; isMe: boolean;
  onSelect: () => void; onCancel: () => void;
}) {
  const { profile } = useNostrProfile(offer.pubkey);
  const [cancelling, setCancelling] = useState(false);

  const tradePrice = bchPriceUSD > 0
    ? calcTradePrice(bchPriceUSD, offer.premiumPct)
    : offer.fixedRateFiat || 0;

  const minUsd = tradePrice > 0 ? satsToUsd(offer.minSats, tradePrice) : null;
  const maxUsd = tradePrice > 0 ? satsToUsd(offer.maxSats, tradePrice) : null;

  const isBuyOffer = offer.side === 'sell'; // they're selling = I can buy

  return (
    <div className={`glass-card transition-all ${isMe ? 'border-primary/30 bg-primary/[0.02]' : 'hover:border-primary/20'}`}>
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <Link to={`/profile/${offer.pubkey}`} className="shrink-0 mt-0.5">
          {profile?.picture
            ? <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            : <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center"><Users className="w-4 h-4 text-primary/50" /></div>}
        </Link>

        <div className="flex-1 min-w-0">
          {/* Name + badges */}
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <Link to={`/profile/${offer.pubkey}`} className="text-sm font-semibold hover:text-primary truncate">
              {profile?.name || offer.pubkey.slice(0, 10) + '...'}
            </Link>
            <UserBadges pubkey={offer.pubkey} size="sm" />
            {isMe && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">You</span>}
            {!!offer.tradeCount && offer.tradeCount > 0 && (
              <span className="text-[10px] text-muted-foreground">{offer.tradeCount} trades</span>
            )}
          </div>

          {/* Price — shown prominently in USD */}
          <div className="flex items-baseline gap-1.5 mb-1">
            {tradePrice > 0 ? (
              <span className="text-base font-bold text-foreground">
                ${tradePrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}<span className="text-xs font-normal text-muted-foreground"> /BCH</span>
              </span>
            ) : (
              <span className="text-sm font-bold text-primary">Market price</span>
            )}
            {offer.premiumPct !== 0 && (
              <span className={`text-xs font-medium ${offer.premiumPct > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {offer.premiumPct > 0 ? '+' : ''}{offer.premiumPct}%
              </span>
            )}
          </div>

          {/* USD range — primary; BCH range secondary */}
          <div className="flex items-center gap-1.5 flex-wrap text-xs text-muted-foreground mb-1.5">
            {minUsd !== null && maxUsd !== null ? (
              <span className="font-medium text-foreground">
                ${minUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} – ${maxUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} {offer.fiatCurrency}
              </span>
            ) : (
              <span>{formatBCH(offer.minSats)} – {formatBCH(offer.maxSats)}</span>
            )}
            <span className="text-muted-foreground/40">·</span>
            <span>{formatBCH(offer.minSats)} – {formatBCH(offer.maxSats)}</span>
          </div>

          {/* Payment methods */}
          <div className="flex flex-wrap gap-1">
            {offer.paymentMethods.map(pm => (
              <span key={pm} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                {PAYMENT_METHOD_LABELS[pm]}
              </span>
            ))}
            {offer.bondPercent > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-0.5">
                <Shield className="w-2.5 h-2.5" /> {offer.bondPercent}% bond
              </span>
            )}
          </div>

          {offer.terms && (
            <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1 italic">"{offer.terms}"</p>
          )}
        </div>

        {/* Action */}
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {isMe ? (
            <button
              onClick={async () => { setCancelling(true); await onCancel(); setCancelling(false); }}
              disabled={cancelling}
              className="px-3 py-1.5 rounded-lg border border-red-500/30 text-red-500 text-xs font-medium hover:bg-red-500/10 disabled:opacity-50"
            >
              {cancelling ? '...' : 'Cancel'}
            </button>
          ) : (
            <button
              onClick={onSelect}
              className={`px-4 py-2 rounded-lg text-xs font-bold text-white shadow-sm ${
                isBuyOffer
                  ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20'
                  : 'bg-primary hover:opacity-90 shadow-primary/20'
              }`}
            >
              {isBuyOffer ? 'Buy' : 'Sell'}
            </button>
          )}
          <span className="text-[10px] text-muted-foreground">{timeAgo(offer.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Post Offer Modal (simplified) ─────────────────────────────────
function PostOfferModal({ bchPriceUSD, onClose, onPosted, publish, user }: {
  bchPriceUSD: number; onClose: () => void; onPosted: () => void;
  publish: any; user: any;
}) {
  // Core fields
  const [side, setSide] = useState<OfferSide>('sell');
  const [minUsd, setMinUsd] = useState('10');
  const [maxUsd, setMaxUsd] = useState('100');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentHandle, setPaymentHandle] = useState('');

  // Advanced (collapsed)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fiatCurrency, setFiatCurrency] = useState('USD');
  const [priceType, setPriceType] = useState<'market' | 'fixed'>('market');
  const [premiumPct, setPremiumPct] = useState(0);
  const [fixedRate, setFixedRate] = useState('');
  const [bondPercent, setBondPercent] = useState(DEFAULT_BOND_PERCENT);
  const [terms, setTerms] = useState('');

  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePM = (pm: PaymentMethod) =>
    setPaymentMethods(prev => prev.includes(pm) ? prev.filter(x => x !== pm) : [...prev, pm]);

  const tradePrice = priceType === 'market' && bchPriceUSD > 0
    ? calcTradePrice(bchPriceUSD, premiumPct)
    : Number(fixedRate) || bchPriceUSD;

  const minSats = usdToSats(Number(minUsd) || 0, tradePrice);
  const maxSats = usdToSats(Number(maxUsd) || 0, tradePrice);

  const handlePost = async () => {
    if (!user) return;
    if (paymentMethods.length === 0) { setError('Select at least one payment method'); return; }
    const minU = Number(minUsd);
    const maxU = Number(maxUsd);
    if (!minU || !maxU || minU > maxU) { setError('Enter valid trade size range'); return; }
    if (minSats < 546) { setError('Minimum is too small (below dust limit)'); return; }
    if (priceType === 'fixed' && !Number(fixedRate)) { setError('Enter a fixed rate'); return; }

    setPosting(true);
    setError(null);
    try {
      const dTag = `exchange-offer-${Date.now()}`;
      await publish({
        kind: OFFER_KIND,
        content: JSON.stringify({
          side,
          priceType,
          premiumPct,
          fixedRateFiat: Number(fixedRate) || undefined,
          fiatCurrency,
          minSats,
          maxSats,
          paymentMethods,
          paymentDetails: paymentHandle.trim() || undefined,
          terms: terms.trim() || undefined,
          status: 'open',
          bondPercent,
        }),
        tags: [
          ['t', EXCHANGE_TAG],
          ['t', 'bitcoincash'],
          ['d', dTag],
          ['alt', `BCH P2P ${side === 'sell' ? 'Sell' : 'Buy'} offer — $${minUsd}–$${maxUsd} ${fiatCurrency}`],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      onPosted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post offer');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-bold text-sm">Post a Trade Offer</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">

          {/* Step 1 — Buy or Sell */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">I want to</label>
            <div className="grid grid-cols-2 gap-2">
              {([
                { v: 'sell', label: 'Sell BCH', sub: 'get fiat', color: 'bg-emerald-500' },
                { v: 'buy',  label: 'Buy BCH',  sub: 'spend fiat', color: 'bg-primary' },
              ] as const).map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setSide(opt.v)}
                  className={`py-3 rounded-xl text-sm font-bold transition-all ${
                    side === opt.v
                      ? `${opt.color} text-white`
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt.label}
                  <div className={`text-[11px] font-normal mt-0.5 ${side === opt.v ? 'opacity-80' : 'text-muted-foreground'}`}>{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2 — Amount in USD */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Trade size (USD)</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Minimum</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <input
                    type="number" value={minUsd} onChange={e => setMinUsd(e.target.value)}
                    min="1" placeholder="10"
                    className="w-full bg-muted rounded-lg pl-6 pr-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Maximum</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <input
                    type="number" value={maxUsd} onChange={e => setMaxUsd(e.target.value)}
                    min="1" placeholder="100"
                    className="w-full bg-muted rounded-lg pl-6 pr-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
            {tradePrice > 0 && minSats > 0 && (
              <p className="text-[11px] text-muted-foreground">
                ≈ {formatBCH(minSats)} – {formatBCH(maxSats)} BCH at ${tradePrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}/BCH
              </p>
            )}
          </div>

          {/* Step 3 — Payment method */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Payment method</label>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map(pm => (
                <button
                  key={pm}
                  onClick={() => togglePM(pm)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    paymentMethods.includes(pm)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {PAYMENT_METHOD_LABELS[pm]}
                </button>
              ))}
            </div>
          </div>

          {/* Step 4 — Payment handle */}
          {paymentMethods.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Your {paymentMethods.length === 1 ? PAYMENT_METHOD_LABELS[paymentMethods[0]] : 'payment'} handle
              </label>
              <input
                type="text" value={paymentHandle} onChange={e => setPaymentHandle(e.target.value)}
                placeholder={
                  paymentMethods.includes('paypal') ? 'e.g. yourname@email.com' :
                  paymentMethods.includes('cashapp') ? 'e.g. $yourcashtag' :
                  paymentMethods.includes('venmo') ? 'e.g. @yourvenmo' :
                  'Your payment details'
                }
                className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {/* Advanced options (collapsed) */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
          >
            <Settings2 className="w-3.5 h-3.5" />
            Advanced options
            {showAdvanced ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
          </button>

          {showAdvanced && (
            <div className="space-y-4 pt-1 border-t border-border">
              {/* Currency */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Fiat currency</label>
                <select value={fiatCurrency} onChange={e => setFiatCurrency(e.target.value)}
                  className="w-full bg-muted rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                  {FIAT_CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              {/* Price type */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Price</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['market', 'fixed'] as const).map(pt => (
                    <button key={pt} onClick={() => setPriceType(pt)}
                      className={`py-2 rounded-lg text-xs font-medium transition-colors ${priceType === pt ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                      {pt === 'market' ? '📈 Market price' : '\uD83D\uDCCC Fixed rate'}
                    </button>
                  ))}
                </div>
                {priceType === 'market' ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <input type="range" min={-10} max={20} value={premiumPct} onChange={e => setPremiumPct(Number(e.target.value))} className="flex-1 accent-primary" />
                      <span className={`text-sm font-bold w-14 text-right ${premiumPct > 0 ? 'text-red-500' : premiumPct < 0 ? 'text-emerald-500' : 'text-foreground'}`}>
                        {premiumPct > 0 ? '+' : ''}{premiumPct}%
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">0% = exact market rate</p>
                  </div>
                ) : (
                  <input type="number" value={fixedRate} onChange={e => setFixedRate(e.target.value)}
                    placeholder={`e.g. ${bchPriceUSD ? Math.round(bchPriceUSD).toString() : '450'}`}
                    className="w-full bg-muted rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
                )}
              </div>

              {/* Bond */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" /> Fidelity bond
                  <span className="text-muted-foreground/60 font-normal">(discourages scams)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input type="range" min={0} max={20} step={1} value={bondPercent}
                    onChange={e => setBondPercent(Number(e.target.value))} className="flex-1 accent-emerald-500" />
                  <span className={`text-sm font-bold w-10 text-right ${bondPercent > 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                    {bondPercent}%
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {bondPercent > 0
                    ? `Both parties deposit ${bondPercent}% of trade value as collateral before trading.`
                    : 'No bond — trust-based only. Recommended for established traders.'}
                </p>
              </div>

              {/* Terms */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Terms (optional)</label>
                <textarea value={terms} onChange={e => setTerms(e.target.value)} rows={2}
                  placeholder="e.g. Trusted traders only. Verified accounts preferred."
                  className="w-full bg-muted rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
              </div>
            </div>
          )}

          {/* Fee notice */}
          <div className="rounded-lg bg-muted/50 p-2.5 text-xs text-muted-foreground flex items-start gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />
            <span>A 0.5% protocol fee is automatically included in each BCH transaction.</span>
          </div>

          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-2.5 text-xs text-red-500 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}

          <button
            onClick={handlePost}
            disabled={posting || paymentMethods.length === 0 || !minUsd || !maxUsd}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {posting
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Plus className="w-4 h-4" />}
            {posting ? 'Posting...' : 'Post Offer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Trade Modal ───────────────────────────────────────────────────
function TradeModal({ offer, bchPriceUSD, tipWallet, user, publish, onClose }: {
  offer: Offer; bchPriceUSD: number; tipWallet: any;
  user: any; publish: any; onClose: () => void;
}) {
  const { profile } = useNostrProfile(offer.pubkey);
  const [amountUsd, setAmountUsd] = useState('');
  const [theirBCHAddress, setTheirBCHAddress] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [txId, setTxId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messageSent, setMessageSent] = useState(false);
  const [step, setStep] = useState<'details' | 'done'>('details');

  const isBuyingBCH = offer.side === 'sell';

  const tradePrice = bchPriceUSD > 0 ? calcTradePrice(bchPriceUSD, offer.premiumPct) : offer.fixedRateFiat || 0;

  // Compute sats from USD input, clamped to offer limits
  const inputSats = useMemo(() => {
    const usd = Number(amountUsd);
    if (!usd || !tradePrice) return 0;
    const s = usdToSats(usd, tradePrice);
    return Math.min(Math.max(s, offer.minSats), offer.maxSats);
  }, [amountUsd, tradePrice, offer.minSats, offer.maxSats]);

  const protocolFee = calcProtocolFee(inputSats);
  const minUsd = tradePrice > 0 ? satsToUsd(offer.minSats, tradePrice) : 0;
  const maxUsd = tradePrice > 0 ? satsToUsd(offer.maxSats, tradePrice) : 0;

  const sendDM = async (msg: string) => {
    if (!user) return;
    try {
      const nostr = (window as any).nostr;
      if (!nostr?.nip04?.encrypt) throw new Error('No NIP-04 signer');
      const ciphertext = await nostr.nip04.encrypt(offer.pubkey, msg);
      await publish({ kind: 4, content: ciphertext, tags: [['p', offer.pubkey]], created_at: Math.floor(Date.now() / 1000) });
      setMessageSent(true);
    } catch (e) {
      setError('Failed to send message: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  const handleSendBCH = async () => {
    if (!theirBCHAddress) { setError('Enter their BCH address'); return; }
    if (!inputSats || inputSats < offer.minSats || inputSats > offer.maxSats) {
      setError(`Amount must be between $${minUsd.toFixed(2)} and $${maxUsd.toFixed(2)}`); return;
    }
    setSending(true);
    setError(null);
    try {
      const txid = await tipWallet.sendBCH(theirBCHAddress, BigInt(inputSats));
      setTxId(txid);
      setStep('done');
      await sendDM(`✅ BCH sent! Trade complete.\nTX: ${txid}\nAmount: ${formatBCH(inputSats)}\n\nThanks for trading on BCH Nostr!`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-primary" />
            {isBuyingBCH ? 'Buy BCH' : 'Sell BCH'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Trader info */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
            {profile?.picture
              ? <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              : <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><Users className="w-5 h-5 text-primary/50" /></div>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{profile?.name || offer.pubkey.slice(0, 14) + '...'}</p>
              <p className="text-xs text-muted-foreground">
                {isBuyingBCH ? 'Selling BCH' : 'Buying BCH'} ·{' '}
                {tradePrice > 0 ? `$${tradePrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}/BCH` : 'Market price'}
                {offer.premiumPct !== 0 && ` (${offer.premiumPct > 0 ? '+' : ''}${offer.premiumPct}%)`}
              </p>
            </div>
            <Link to={`/profile/${offer.pubkey}`} className="p-1.5 rounded-lg hover:bg-muted shrink-0">
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </Link>
          </div>

          {step === 'done' ? (
            <div className="text-center py-8 space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-emerald-500" />
              </div>
              <p className="font-bold">Trade Initiated!</p>
              <p className="text-xs text-muted-foreground">BCH sent. A confirmation message has been sent to the trader via encrypted DM.</p>
              {txId && (
                <a href={`https://bchexplorer.cash/tx/${txId}`} target="_blank" rel="noopener"
                  className="flex items-center justify-center gap-1.5 text-xs text-primary hover:underline">
                  <ExternalLink className="w-3.5 h-3.5" /> View on explorer
                </a>
              )}
              <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90">Done</button>
            </div>
          ) : (
            <>
              {/* Amount in USD */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Amount (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                  <input
                    type="number"
                    value={amountUsd}
                    onChange={e => setAmountUsd(e.target.value)}
                    placeholder={minUsd > 0 ? minUsd.toFixed(0) : '10'}
                    className="w-full bg-muted rounded-xl pl-7 pr-3 py-3 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>
                    {inputSats > 0 ? `≈ ${formatBCH(inputSats)} BCH` : 'Enter amount'}
                  </span>
                  <span>
                    Range: ${minUsd.toFixed(0)} – ${maxUsd.toFixed(0)}
                  </span>
                </div>
              </div>

              {/* Fee breakdown */}
              {inputSats > 0 && (
                <div className="rounded-xl bg-muted/30 p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">BCH amount</span>
                    <span className="font-mono font-medium">{formatBCH(inputSats)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Protocol fee (0.5%)</span>
                    <span className="font-mono text-muted-foreground">{formatBCH(protocolFee)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-border pt-1.5">
                    <span>Total BCH</span>
                    <span className="font-mono text-primary">{formatBCH(inputSats + protocolFee)}</span>
                  </div>
                </div>
              )}

              {/* Payment methods */}
              <div className="rounded-xl bg-muted/50 p-3 space-y-1.5">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Payment via</p>
                <div className="flex flex-wrap gap-1">
                  {offer.paymentMethods.map(pm => (
                    <span key={pm} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                      {PAYMENT_METHOD_LABELS[pm]}
                    </span>
                  ))}
                </div>
                {offer.paymentDetails && <p className="text-xs font-medium">{offer.paymentDetails}</p>}
                {offer.terms && <p className="text-[10px] text-muted-foreground italic">"{offer.terms}"</p>}
              </div>

              {/* BCH address (if selling BCH) */}
              {!isBuyingBCH && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Send BCH to</label>
                  <input
                    type="text" value={theirBCHAddress} onChange={e => setTheirBCHAddress(e.target.value)}
                    placeholder="bitcoincash:q..."
                    className="w-full bg-muted rounded-xl px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}

              {/* Message trader */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {isBuyingBCH ? 'Message seller' : 'Message buyer'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text" value={chatMessage} onChange={e => setChatMessage(e.target.value)}
                    placeholder={`Hi! I'd like to ${isBuyingBCH ? 'buy' : 'sell'}${amountUsd ? ` $${amountUsd}` : ''} via ${offer.paymentMethods[0] ? PAYMENT_METHOD_LABELS[offer.paymentMethods[0]] : 'your payment method'}`}
                    className="flex-1 bg-muted rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    onClick={() => sendDM(chatMessage || `Hi! I'd like to ${isBuyingBCH ? 'buy' : 'sell'}${amountUsd ? ` $${amountUsd} worth of` : ''} BCH.`)}
                    className="px-3 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 hover:opacity-90"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    {messageSent ? '✓ Sent' : 'Send'}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground">Sent as an encrypted Nostr DM.</p>
              </div>

              {/* Next steps for buyers */}
              {isBuyingBCH && (
                <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs space-y-1">
                  <p className="font-semibold text-primary">Next steps</p>
                  <p className="text-muted-foreground">1. Message the seller above to confirm details</p>
                  <p className="text-muted-foreground">2. Send ${amountUsd || '...'} via {offer.paymentMethods.map(pm => PAYMENT_METHOD_LABELS[pm]).join(' or ')}</p>
                  <p className="text-muted-foreground">3. Share your BCH address so they can send you BCH</p>
                </div>
              )}

              {/* Wallet balance + send button for sellers */}
              {!isBuyingBCH && tipWallet.ready && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs px-1">
                    <span className="text-muted-foreground flex items-center gap-1"><Wallet className="w-3 h-3" /> Wallet balance</span>
                    <span className="font-mono font-semibold">{formatBCH(Number(tipWallet.balanceSats))}</span>
                  </div>
                  <button
                    onClick={handleSendBCH}
                    disabled={sending || !theirBCHAddress || !inputSats}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white font-bold text-sm hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    {sending
                      ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <ArrowLeftRight className="w-4 h-4" />}
                    {sending ? 'Sending...' : inputSats ? `Send ${formatBCH(inputSats)}` : 'Enter amount'}
                  </button>
                </div>
              )}

              {error && (
                <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-2.5 text-xs text-red-500 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

